import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listSupplierPurchases } from '../services/suppliersService';
import { formatDate, formatMoney } from '../lib/format';
import { friendlyError } from '../lib/errors';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';

export default function SupplierDetailPage() {
  const { ruc: proveedorRucParam } = useParams();
  const proveedorRuc = decodeURIComponent(proveedorRucParam || '');
  const { ruc } = useAuth();
  const navigate = useNavigate();
  const [compras, setCompras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCompras(await listSupplierPurchases({ ruc, proveedorRuc, pageSize: 20 }));
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [ruc, proveedorRuc]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <LoadingState label="Cargando proveedor…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const nombre = compras[0]?.proveedorRazonSocial || 'Proveedor';
  const total = compras.reduce((s, c) => s + Number(c.total || 0), 0);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <p className="breadcrumb">
            <Link to="/admin/proveedores">Proveedores</Link> / {proveedorRuc}
          </p>
          <h1>{nombre}</h1>
          <p className="muted">RUC {proveedorRuc}</p>
        </div>
        <div className="row-actions">
          <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
            Volver
          </button>
          <Link className="btn btn-primary" to="/admin/compras/nueva">
            Nueva compra
          </Link>
        </div>
      </header>

      <section className="stat-grid compact">
        <div className="stat-card">
          <p className="stat-label">Compras</p>
          <p className="stat-value">{compras.length}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Total histórico</p>
          <p className="stat-value">{formatMoney(total)}</p>
        </div>
      </section>

      <section className="panel">
        <header className="panel-header">
          <h2>Compras de este proveedor</h2>
          <Link to="/admin/compras">Todas las compras</Link>
        </header>
        {compras.length === 0 ? (
          <EmptyState title="Sin compras" />
        ) : (
          <ul className="simple-list">
            {compras.map((c) => (
              <li key={c.id}>
                <div>
                  <strong>
                    {c.serie}-{c.correlativo}
                  </strong>
                  <div className="muted">{formatDate(c.fechaEmision || c.createdAt)}</div>
                </div>
                <div className="row-end">
                  <span>{formatMoney(c.total)}</span>
                  <Link className="btn btn-mini" to={`/admin/compras/${c.id}`}>
                    Ver
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
